import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
    Flex, Stack, Text, Icon, IconButton, Tooltip, Divider, Box, useColorModeValue,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdPowerSettingsNew, MdHistory } from "react-icons/md";
import Card from "horizon/components/card/Card";
import { getTypeLabel } from "./legalDocumentConstants";
import LegalStatusBadge from "./LegalStatusBadge";
import DetailMetadataPanel from "./DetailMetadataPanel";
import DetailContentSections from "./DetailContentSections";
import VersionHistory from "./VersionHistory";
import LegalVersionsSnapshots from "./LegalVersionsSnapshots";

/**
 * Renders the empty state when no document is selected.
 * @returns {React.ReactElement}
 */
function EmptyState() {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Card h="100%">
            <Flex align="center" justify="center" minH="300px">
                <Text color={color} fontSize="0.9rem">Sélectionnez un document pour voir les détails</Text>
            </Flex>
        </Card>
    );
}

/**
 * Renders the action buttons toolbar (edit, toggle active, delete, history).
 * @param {object} props
 * @param {boolean} props.isActive - Whether the document is currently active
 * @param {function} props.onEdit - Callback to open edit form
 * @param {function} props.onToggleActive - Callback to activate/deactivate
 * @param {function} props.onDelete - Callback to delete the document
 * @param {boolean} props.showHistory - Whether history panel is visible
 * @param {function} props.onToggleHistory - Callback to toggle history panel
 * @param {object} props.doc - The legal document object
 * @returns {React.ReactElement}
 */
function DetailActions({ isActive, onEdit, onToggleActive, onDelete, showHistory, onToggleHistory, doc }) {
    return (
        <Stack direction="row" spacing="2px">
            <ActionButton title="Edit (creates new version)" onClick={onEdit} color="brand.500" icon={MdEdit} />
            <ActionButton title={isActive ? "Deactivate" : "Activate"} onClick={() => onToggleActive(doc)}
                color={isActive ? "orange.400" : "green.400"} icon={MdPowerSettingsNew} />
            <ActionButton title="Delete" onClick={() => onDelete(doc)} color="red.400"
                icon={MdDelete} isDisabled={isActive} />
            <ActionButton title="Version history" onClick={onToggleHistory}
                color={showHistory ? "brand.500" : "secondaryGray.500"} icon={MdHistory} />
        </Stack>
    );
}

/**
 * Renders a single icon button with tooltip for the detail actions toolbar.
 * @param {object} props
 * @param {string} props.title - Tooltip text
 * @param {function} props.onClick - Click handler
 * @param {string} props.color - Icon color token
 * @param {React.ElementType} props.icon - react-icons component
 * @param {boolean} [props.isDisabled] - Whether the button is disabled
 * @returns {React.ReactElement}
 */
function ActionButton({ title, onClick, color, icon, isDisabled }) {
    return (
        <Tooltip label={title}>
            <IconButton aria-label={title} size="sm" variant="ghost" color={color}
                icon={<Icon as={icon} />} onClick={onClick} isDisabled={isDisabled} />
        </Tooltip>
    );
}

/**
 * Renders the version history section header and content panel.
 * @param {object} props
 * @param {Array} props.versions - Version history entries
 * @param {boolean} props.loading - Whether history is loading
 * @param {number} props.currentId - Current document ID for highlighting
 * @returns {React.ReactElement}
 */
function HistorySection({ versions, loading, currentId }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    return (
        <>
            <Stack direction="row" spacing="8px" align="center" mb="12px">
                <Icon as={MdHistory} color="brand.500" boxSize="18px" />
                <Text color={labelColor} fontSize="0.8rem" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px">
                    Version History
                </Text>
            </Stack>
            <Box p="16px" borderRadius="14px" mb="16px" bg={boxBg}>
                <VersionHistory versions={versions} loading={loading} currentId={currentId} />
            </Box>
        </>
    );
}

/**
 * Renders the detail header row with status badge, document type, and action buttons.
 * @param {object} props - Header props forwarded from the detail card
 * @returns {React.ReactElement}
 */
function DetailHeaderRow({ doc, isActive, showHistory, onEdit, onToggleActive, onDelete, onToggleHistory }) {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex justify="space-between" align="center" mb="16px">
            <Stack direction="row" spacing="8px" align="center">
                <LegalStatusBadge status={doc.status} />
                <Text color={titleColor} fontWeight="700" fontSize="1.1rem">{getTypeLabel(doc.type)}</Text>
            </Stack>
            <DetailActions isActive={isActive} onEdit={onEdit} onToggleActive={onToggleActive}
                onDelete={onDelete} showHistory={showHistory} onToggleHistory={onToggleHistory} doc={doc} />
        </Flex>
    );
}

/**
 * Renders the inner card content for the document detail view.
 * @param {object} props - All detail props forwarded from LegalDocumentDetail
 * @returns {React.ReactElement}
 */
function DetailCardContent({ doc, showHistory, onEdit, onToggleActive, onDelete, onToggleHistory, versions, historyLoading }) {
    const dividerColor = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    return (
        <Card h="100%">
            <DetailHeaderRow doc={doc} isActive={doc.status === "active"} showHistory={showHistory}
                onEdit={onEdit} onToggleActive={onToggleActive} onDelete={onDelete} onToggleHistory={onToggleHistory} />
            <DetailMetadataPanel doc={doc} />
            {showHistory && <HistorySection versions={versions} loading={historyLoading} currentId={doc.id} />}
            <LegalVersionsSnapshots docId={doc.id} />
            <Divider borderColor={dividerColor} mb="16px" />
            <DetailContentSections sections={doc.content?.sections || []} />
        </Card>
    );
}

/**
 * Displays the detail view of a selected legal document with metadata,
 * content sections, and version history panel.
 * @param {object} props
 * @param {object} props.document - The legal document to display
 * @param {function} props.onEdit - Callback to open edit form
 * @param {function} props.onToggleActive - Callback to activate/deactivate
 * @param {function} props.onDelete - Callback to delete the document
 * @returns {React.ReactElement}
 */
function LegalDocumentDetail({ document: doc, onEdit, onToggleActive, onDelete }) {
    const [versions, setVersions] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const fetchHistory = useCallback(async () => {
        if (!doc?.id) return;
        setHistoryLoading(true);
        try { setVersions((await axios.get(`/admin/legal/history/${doc.id}`)).data || []); }
        catch { setVersions([]); }
        finally { setHistoryLoading(false); }
    }, [doc?.id]);

    useEffect(() => { if (showHistory && doc?.id) fetchHistory(); }, [showHistory, doc?.id, fetchHistory]);
    useEffect(() => { setShowHistory(false); setVersions([]); }, [doc?.id]);

    if (!doc) return <EmptyState />;

    return (
        <DetailCardContent doc={doc} showHistory={showHistory} onEdit={onEdit} onToggleActive={onToggleActive}
            onDelete={onDelete} onToggleHistory={() => setShowHistory(!showHistory)}
            versions={versions} historyLoading={historyLoading} />
    );
}

export default LegalDocumentDetail;
